import { Link } from 'react-router-dom';
import {
  AlertTriangle, Award, BookOpen, CalendarPlus, CheckCircle2, ClipboardList,
  Clock, Eye, Flag, Lightbulb, Plus, ShieldCheck, TrendingUp, Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { SYSTEM_ROLES } from '@lms/shared';
import { Card } from '@/components/ui/card';
import { gateActions, type QuickAction } from '@/components/dashboard/quickActions';
import { cn } from '@/lib/utils';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT AN ADMINISTRATOR OPENS THE PRODUCT TO SEE
 *
 * Not "how am I doing" — that is the learner's dashboard, and an administrator
 * was being shown it, with a team rollup bolted underneath. This answers the
 * two questions they actually have: how is the organisation doing, and what
 * needs me today.
 *
 * Every figure is measured. There is no certificate expiry in this system, so
 * there is no "expiring soon" tile however neatly one would complete the row —
 * a dashboard that invents a number is worse than one with four tiles.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export interface AdminData {
  compliance: {
    /** People who have finished ALL their mandatory training, as a percentage. */
    percent: number;
    target: number;
    pointsShort: number;
    /** The numerator and denominator behind `percent`, both in people. */
    peopleCompliant: number;
    peopleTotal: number;
    /** Assignments — the work behind the people figure. */
    completed: number;
    total: number;
    overdue: number;
    /** People behind a deadline — who to talk to, not how many rows are late. */
    peopleOverdue: number;
  };
  activeLearners: {
    active: number;
    headcount: number;
    percentOfOrg: number;
    /** `September 2026` — the window as a name, not a day count. */
    windowLabel: string;
    /** The month it is compared against. */
    priorLabel: string;
    /** The same elapsed span of the previous month. */
    priorActive: number;
    /** Null where the previous window was empty — see the service. */
    changePercent: number | null;
  };
  courses: { published: number; draft: number; archived: number; mandatory: number };
  certificates: { issued: number; thisMonth: number };
  /** Open question objections — the other queue with somebody waiting on it. */
  openRebuttals: number;
  /** Certificates an answer-key change put in doubt; a person must decide. */
  certificatesToReview: number;
  /** False when nobody has set tomorrow's Learning Spark. */
  challengeScheduledTomorrow: boolean;
  /**
   * People whose latest sitting on some paper was a failure.
   *
   * The task built on this is also the route to the Action Center now that it
   * has left the sidebar — see the note in `navigation.ts`.
   */
  failedAssessments: number;
  learningHours: {
    hours: number;
    priorHours: number;
    windowLabel: string;
    priorLabel: string;
    changePercent: number | null;
  };
  mandatoryCourses: {
    id: string;
    title: string;
    total: number;
    completed: number;
    percent: number;
    dueDate: string | null;
  }[];
  /**
   * Mandatory compliance per department, worst first.
   *
   * Not the same figure as `TeamData.departments`, which counts every
   * assignment — see `DepartmentCompliance` for why the two must not be
   * confused on one screen.
   */
  departments: {
    departmentId: string;
    name: string;
    headcount: number;
    /** Assignments. */
    total: number;
    completed: number;
    /** People — the same measure as the headline compliance figure. */
    peopleTotal: number;
    peopleCompliant: number;
    /** People behind a deadline here — the breakdown under the overdue task. */
    peopleOverdue: number;
    percent: number;
    overdue: number;
    head: string | null;
    otherHeads: number;
  }[];
}

/**
 * Thousands as `1.8k`, so a five-digit figure cannot break a hero tile.
 *
 * Exported because the tiles themselves live on the dashboard page, next to
 * the `HeroStat` an employee gets — the admin's row is the same component with
 * different figures in it, not a second one that merely looks similar.
 */
export function compactNumber(value: number): string {
  if (value < 1000) return String(Math.round(value));
  return `${(value / 1000).toFixed(1)}k`;
}

// ═══════════════════════════════════════════════════════════════════════════
// WHAT NEEDS YOUR ATTENTION — the priority list
// ═══════════════════════════════════════════════════════════════════════════

/** How urgent a task is, which decides the badge and nothing else. */
type Urgency = 'critical' | 'waiting' | 'action' | 'review';

export interface Task {
  id: string;
  icon: LucideIcon;
  title: string;
  /**
   * A whole sentence with the figure in it — never a bare number.
   *
   * Omitted where a `progress` bar says the same thing better. "65 of 65
   * people have mandatory training still to finish — 95 points below the 95%
   * target" is a sentence somebody has to parse; a bar two thirds empty with a
   * target mark on it is read in one glance.
   */
  detail?: string;
  /** A measured position against a goal, drawn rather than described. */
  progress?: {
    percent: number;
    /** Marked on the bar, so "how far off" needs no arithmetic. */
    target?: number;
    /** The figures behind it, in a few words. */
    caption: string;
  };
  /**
   * Where the row goes, in words.
   *
   * No longer printed under the task — six of those turned the panel into a
   * list of sentences. It is the link's `title` and part of its `aria-label`
   * instead, so a hover and a screen reader both still say where the arrow
   * leads, which an arrow on its own cannot.
   */
  cta: string;
  to: string;
  urgency: Urgency;
  badge: string;
  requires: string;
  /**
   * Shown even with nothing waiting, drawn as done rather than as a job.
   *
   * The three queues below are PERMANENT rows. Every other task here appears
   * only when its own figure is non-zero, which is right for an alarm — a
   * standing "0 people overdue" trains the eye to skip the panel, and the day
   * it says 8 nobody looks.
   *
   * The Evaluation Queue and Learning Spark are different because this panel
   * is now their ONLY route: both came off the Admin sidebar on the grounds
   * that they have a card here, and a card that hides itself the moment the
   * queue is clear is not a route — it is a route that exists exactly when you
   * do not need it. So those two stay, and say they are clear.
   *
   * Question reports is NOT one of them, deliberately: objections are rare
   * enough that a standing "Clear" row was saying nothing on almost every day.
   * See the note where it is pushed.
   */
  cleared?: boolean;
}

/*
  There was a `breakdown` here — department chips under the task, naming where
  the problem sat. Taken off by request: the departments have their own report
  a click away, and the chips were a third row of detail on a card whose job is
  to say a thing needs doing, not to analyse it.

  The API still returns `peopleOverdue` and the compliant counts per
  department, so putting them back is a `.map` rather than a query.
*/

const URGENCY: Record<Urgency, { badge: string; icon: string }> = {
  critical: { badge: 'bg-destructive/10 text-destructive', icon: 'text-destructive' },
  waiting: { badge: 'bg-clay-50 text-clay-700 dark:bg-clay-400/15 dark:text-clay-200', icon: 'text-clay-600 dark:text-clay-300' },
  action: { badge: 'bg-brand-50 text-brand-700 dark:bg-brand-400/15 dark:text-brand-200', icon: 'text-brand-700 dark:text-brand-300' },
  review: { badge: 'bg-muted text-muted-foreground', icon: 'text-muted-foreground' },
};

/**
 * What is waiting, plus three rows that are always here.
 *
 * Most entries are CONDITIONAL on their own figure being non-zero, which is
 * the difference between a task list and a menu. A standing row reading
 * "0 people overdue" trains the eye to skip the panel, and the one day it says
 * 8 nobody looks.
 *
 * The three queues are the exception, and `cleared` on `Task` says why: this
 * panel is their only route now that they are off the sidebar, so a card that
 * hides itself once the queue empties is a route that exists exactly when it
 * is not needed. They stay, and say they are clear.
 *
 * Ordered by who is waiting and for how long: people already past a deadline,
 * then people waiting on a decision, then work that has not gone out yet —
 * and the cleared rows last, sorted in `AttentionTasks`.
 */
/**
 * A trainer's own compliance, when the reader is one.
 *
 * Same three numbers the organisation's task reads, measured over the content
 * this person wrote rather than over the whole company — see
 * `trainer-desk.service.ts`.
 */
export interface OwnCompliance {
  percent: number;
  peopleCompliant: number;
  peopleTotal: number;
}

export function priorityTasks(
  admin: AdminData,
  pendingEvaluations: number,
  /**
   * Supplied only for a trainer. When present the compliance task is about
   * THEIR courses and assessments; the rest of the panel is unchanged.
   */
  own?: OwnCompliance | null,
): Task[] {
  const tasks: Task[] = [];

  if (admin.compliance.peopleOverdue > 0) {
    const n = admin.compliance.peopleOverdue;

    tasks.push({
      id: 'overdue',
      icon: AlertTriangle,
      title: 'Mandatory training overdue',
      detail: `${n} ${n === 1 ? 'employee has' : 'employees have'} overdue mandatory training.`,
      cta: 'Review learners',
      to: '/admin/action-center',
      urgency: 'critical',
      badge: 'Critical',
      requires: 'scope.organization',
    });
  }

  /**
   * Training that is simply not done, which is a different task from training
   * that is LATE — and on most systems the only one that ever fires.
   *
   * Overdue needs a due date, and an assignment made without one can never be
   * overdue however long it sits. So a panel carrying only the overdue task
   * stayed silent through an organisation at 0% compliance, while the figure
   * for it sat in the hero as a sentence nobody could act on.
   */
  /**
   * ── WHOSE TRAINING IS INCOMPLETE ─────────────────────────────────────────
   *
   * A trainer reads this row about their OWN content. It said "1 of 64 people
   * complete" — the organisation's mandatory training — on the dashboard of
   * somebody who did not assign any of it and cannot chase it, directly above
   * a Compliance tile showing a different number for their own work. Two
   * figures, one heading, neither explaining the other.
   *
   * The target and the twenty-point threshold stay shared: what counts as
   * "critical" is an organisation policy, not a per-author one.
   */
  const compliance = own
    ? { ...own, target: admin.compliance.target }
    : admin.compliance;
  const pointsShort = compliance.target - compliance.percent;

  /**
   * ── PERMANENT, like the two queues below it ──────────────────────────────
   *
   * This used to appear only while compliance was BELOW target, which made it
   * an alarm. By request it is now a standing row: the panel shows the same
   * three things every day, and an administrator who has met the target still
   * gets to see that they have, rather than a panel that silently lost a line.
   *
   * The state is carried in the badge and the urgency rather than by existing
   * or not, and the bar keeps its target marker in every state — "98% / 95%"
   * is the reassurance, and it cannot be read without the comparison.
   */
  {
    const { peopleCompliant, peopleTotal, target } = compliance;
    const outstanding = peopleTotal - peopleCompliant;
    const met = peopleTotal > 0 && compliance.percent >= target;
    /* Nothing assigned is NOT a failure. Somebody holding no mandatory
       training is neither compliant nor in breach — see the memory note on
       what this figure counts — so it reads as settled, not as 0%. */
    const nothingAssigned = peopleTotal === 0;

    tasks.push({
      id: 'compliance',
      icon: ShieldCheck,
      title: nothingAssigned
        ? own
          ? 'Nothing assigned yet'
          : 'No mandatory training assigned'
        : met
          ? own
            ? 'Your training is on target'
            : 'Mandatory training on target'
          : own
            ? 'Your training is incomplete'
            : 'Mandatory training incomplete',
      ...(nothingAssigned
        ? {
            detail: own
              ? 'Nobody holds a course or assessment you wrote yet.'
              : 'No mandatory course has been assigned to anyone yet.',
          }
        : {
            /* A bar rather than a sentence: the gap to target is a distance,
               and a distance is a thing to look at, not to read. */
            progress: {
              percent: compliance.percent,
              target,
              caption: met
                ? `${peopleCompliant} of ${peopleTotal} people complete`
                : `${peopleCompliant} of ${peopleTotal} people complete · ${outstanding} to go`,
            },
          }),
      cta: own ? 'Open the course report' : 'Open the department report',
      /* Per-course completion for a trainer: the department breakdown is
         about people they do not govern, and their figure is about content. */
      to: own ? '/admin/reports?type=course' : '/admin/reports',
      /* Twenty points is the same threshold the compliance panel colours on,
         so one number does not read as amber in one place and red in another. */
      urgency: nothingAssigned || met ? 'review' : pointsShort > 20 ? 'critical' : 'action',
      badge: nothingAssigned ? 'None assigned' : met ? 'On target' : pointsShort > 20 ? 'Critical' : 'Below target',
      /* Settled rows sink below the work — see `cleared`. */
      ...(nothingAssigned || met ? { cleared: true } : {}),
      requires: 'report.view',
    });
  }

  tasks.push(
    pendingEvaluations > 0
      ? {
          id: 'evaluations',
          icon: ClipboardList,
          title: 'Evaluations awaiting review',
          detail: 'Assessments requiring manual evaluation.',
          cta: 'Open evaluation queue',
          to: '/admin/evaluation',
          urgency: 'waiting',
          badge: `${pendingEvaluations} waiting`,
          requires: 'evaluation.view_queue',
        }
      : {
          id: 'evaluations',
          icon: ClipboardList,
          title: 'Evaluation queue',
          detail: 'Nothing waiting to be marked.',
          cta: 'Open evaluation queue',
          to: '/admin/evaluation',
          urgency: 'review',
          badge: 'Clear',
          requires: 'evaluation.view_queue',
          cleared: true,
        },
  );

  /**
   * Only when somebody has actually objected — by request, and it is the right
   * call for this one.
   *
   * It was made permanent along with the other two queues, on the grounds that
   * this panel is their only route. That reasoning holds for them and not for
   * this: objections are RARE, so a standing "Question reports · Clear" was a
   * row saying nothing on almost every day, adding a fourth line to a panel
   * whose whole job is to be short.
   *
   * What it costs is that an Admin with no objections has no menu route to
   * `/admin/question-reports` at all. That screen is empty in exactly that
   * case, so there is nothing to reach — and the row appears the moment there
   * is.
   */
  if (admin.openRebuttals > 0) {
    const openReports = admin.openRebuttals;
    tasks.push({
      id: 'rebuttals',
      icon: Flag,
      title: 'Question reports open',
      detail: `${openReports} ${
        openReports === 1 ? 'objection is' : 'objections are'
      } waiting on a decision.`,
      cta: 'Review reports',
      to: '/admin/question-reports',
      urgency: 'waiting',
      badge: `${openReports} open`,
      requires: 'rebuttal.view',
    });
  }

  if (admin.certificatesToReview > 0) {
    const n = admin.certificatesToReview;
    tasks.push({
      id: 'certificates',
      icon: Award,
      title: 'Certificates flagged for review',
      detail: `${n} ${n === 1 ? 'certificate was' : 'certificates were'} issued on an attempt that now fails.`,
      cta: 'Review certificates',
      to: '/admin/certificates',
      urgency: 'critical',
      badge: 'Decide',
      requires: 'certificate.view',
    });
  }

  /**
   * Failed assessments are NOT a task, and used to be listed as one.
   *
   * Everything else on this panel is something somebody must act on — mark
   * this, decide that, chase the other. A failed result needs no action from
   * an administrator: the attempt was marked, the outcome is a fact, and the
   * person may simply retake it. Listing it here meant the panel never
   * emptied and the rows that DO need acting on sat beside one that does not.
   *
   * It lives on the Evaluated tab of the evaluation queue instead — the list
   * of marked attempts, filtered to the ones that failed, which is where
   * somebody asking "who failed" is already looking.
   */

  tasks.push(
    !admin.challengeScheduledTomorrow
      ? {
          id: 'spark',
          icon: Lightbulb,
          title: 'Learning Spark not scheduled',
          detail: 'No question is scheduled for tomorrow.',
          cta: 'Schedule a question',
          to: '/admin/learning-spark',
          urgency: 'action',
          badge: 'Action needed',
          requires: 'challenge.manage',
        }
      : {
          id: 'spark',
          icon: Lightbulb,
          title: 'Learning Spark',
          detail: "Tomorrow's question is set.",
          cta: 'Open Learning Spark',
          to: '/admin/learning-spark',
          urgency: 'review',
          badge: 'Scheduled',
          requires: 'challenge.manage',
          cleared: true,
        },
  );

  if (admin.courses.draft > 0) {
    const n = admin.courses.draft;
    tasks.push({
      id: 'drafts',
      icon: BookOpen,
      title: 'Courses in draft',
      detail: `${n} ${n === 1 ? 'course is' : 'courses are'} ready for publishing.`,
      cta: 'Review drafts',
      to: '/admin/courses',
      urgency: 'review',
      badge: 'Review',
      requires: 'course.view',
    });
  }

  return tasks;
}

export function AttentionTasks({
  admin,
  permissions,
  pendingEvaluations,
  ownCompliance,
}: {
  admin: AdminData;
  permissions: string[];
  pendingEvaluations: number;
  /** A trainer's own figure. Absent for everybody else. */
  ownCompliance?: OwnCompliance | null;
}) {
  const held = new Set(permissions);

  /**
   * Cleared rows sink to the bottom.
   *
   * They are permanent so their screen stays reachable — see `cleared` on
   * `Task` — but they are not work, and a settled queue sitting above an
   * overdue one would be the panel arguing with its own heading. `priorityTasks`
   * already returns the rest in order of who is waiting and for how long, so
   * this only moves the done ones out of the way.
   */
  const tasks = priorityTasks(admin, pendingEvaluations, ownCompliance)
    .filter((t) => held.has(t.requires))
    .sort((a, b) => Number(Boolean(a.cleared)) - Number(Boolean(b.cleared)));

  return (
    /* `h-full` so the pair share a height whichever of them is taller — see
       the note on `QuickActions`. */
    <Card className="flex h-full flex-col overflow-hidden">
      {/*
        A dark header, and no count.

        The count was a number about a list that is already right there — you
        can see there are two — and it sat in the corner competing with the
        badges on the rows, which are the ones that mean something.

        Dark because this panel is the one thing on the page asking to be
        acted on rather than read. `bg-brand-800` is the same green as the
        Learning Spark tile and the primary button, not a new dark for this
        one card.
      */}
      {/* Tightened to py-3 from py-3.5, along with the rows below and the
          whole of Quick actions: together the pair stood a little taller than
          the space under the hero, so the last row of the shorter card was
          being clipped by the fold on a laptop screen. */}
      <div className="shrink-0 bg-brand-800 px-4 py-3 text-white">
        <h2 className="text-[15px] font-semibold">What needs your attention</h2>
        <p className="mt-0.5 text-xs text-white/70">Priority tasks across your LMS</p>
      </div>

      {tasks.length === 0 ? (
        /* Says WHY it is empty. A blank panel under this heading reads as
           something that failed to load, and "nothing is waiting" is a
           genuinely good piece of news that deserves saying. */
        /*
          Only reachable now for somebody holding none of the permissions the
          three permanent rows need — every task here is gated on one. It no
          longer claims the queues are clear, because for this reader they were
          never shown at all.
        */
        <div className="flex items-center gap-3 px-5 py-8">
          <CheckCircle2 className="h-5 w-5 shrink-0 text-success" aria-hidden />
          <div>
            <p className="text-sm font-medium">Nothing is waiting on you</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              No overdue training and nothing needing a decision.
            </p>
          </div>
        </div>
      ) : (
        <ul className="divide-y">
          {tasks.map((task) => {
            const Icon = task.icon;
            const tone = URGENCY[task.urgency];

            return (
              <li key={task.id}>
                {/*
                  The whole row is the link, and the arrow at its end is the
                  only thing that says so.

                  A line of text spelled the destination out — "Open the
                  department report" — under every task. Six of those and the
                  panel read as a list of sentences rather than a list of
                  problems. It is not lost: it is the link's own label, so it
                  is what a hover says and what a screen reader announces.
                */}
                <Link
                  to={task.to}
                  title={task.cta}
                  aria-label={`${task.title} — ${task.cta}`}
                  className={cn(
                    'flex items-start gap-3 px-4 py-3 transition-colors hover:bg-muted/40',
                    /* Settled, not urgent. Still a full-strength link — this is
                       the only way to the screen — but it should not compete
                       with the rows that do need somebody. */
                    task.cleared && 'py-2.5',
                  )}
                >
                  {task.cleared ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden />
                  ) : (
                    <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', tone.icon)} aria-hidden />
                  )}

                    <div className="min-w-0 flex-1">
                      <p
                        className={cn(
                          'text-[13px] leading-tight',
                          task.cleared ? 'font-medium text-muted-foreground' : 'font-semibold',
                        )}
                      >
                        {task.title}
                      </p>

                      {task.detail && (
                        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                          {task.detail}
                        </p>
                      )}

                      {task.progress && (
                        <div className="mt-2">
                          <div className="flex items-center gap-2">
                            {/* `relative` so the target mark can sit on the
                                track rather than beside it — the distance
                                between fill and mark IS the message. */}
                            <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                              <div
                                className={cn(
                                  'h-full rounded-full transition-[width] duration-500',
                                  task.progress.target !== undefined &&
                                    task.progress.percent >= task.progress.target
                                    ? 'bg-success'
                                    : tone.icon.replace('text-', 'bg-'),
                                )}
                                style={{ width: `max(2px, ${task.progress.percent}%)` }}
                              />
                              {task.progress.target !== undefined && (
                                <span
                                  className="absolute inset-y-0 w-px bg-foreground/40"
                                  style={{ left: `${task.progress.target}%` }}
                                  aria-hidden
                                />
                              )}
                            </div>

                            <span className="shrink-0 font-mono text-xs font-semibold tabular-nums">
                              {Math.round(task.progress.percent)}%
                            </span>
                            {task.progress.target !== undefined && (
                              <span className="shrink-0 font-mono text-[10px] text-muted-foreground">
                                / {task.progress.target}%
                              </span>
                            )}
                          </div>

                          <p className="mt-1 text-[11px] text-muted-foreground">
                            {task.progress.caption}
                          </p>
                        </div>
                      )}

                    </div>

                  {/* Badge then arrow, stacked at the right: what KIND of
                      problem this is, and that there is somewhere to go about
                      it. Both belong to the row, so both sit outside the
                      content column that may grow a bar and chips. */}
                  <span className="flex shrink-0 flex-col items-end gap-2">
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-[11px] font-medium',
                        tone.badge,
                      )}
                    >
                      {task.badge}
                    </span>
                    <ArrowOut className="h-3.5 w-3.5 text-muted-foreground" />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// QUICK ACTIONS — the three things an administrator makes
// ═══════════════════════════════════════════════════════════════════════════

/*
  `QuickAction`, `QuickLink` and the permission gating moved to
  `quickActions.ts` when a Manager's dashboard grew a panel of its own. The
  gating is the part worth sharing — it is what keeps a row from offering a
  button the API would refuse — and there it can be tested against the real
  role presets without rendering anything.

  The CARD is not shared, and deliberately: this one lives in a narrow column
  beside the attention band and stretches to match its height, which is the
  opposite of what a panel in the wide column needs. See
  `ManagerQuickActions`.
*/

/**
 * Making things, kept apart from the tasks beside it.
 *
 * These never "need attention" — nothing is waiting, nobody is overdue. Mixed
 * into that list they were cards where some were chores and some were
 * shortcuts, and the panel meant neither thing clearly.
 *
 * Two buttons per row, because "create" and "look at what exists" are the two
 * things anybody wants from a list and only one of them was offered.
 */
const QUICK_ACTIONS: QuickAction[] = [
  {
    label: 'Courses',
    hint: 'Folders, resources and rules',
    icon: BookOpen,
    view: { to: '/admin/courses', requires: 'course.view', label: 'View all courses' },
    create: { to: '/admin/courses?new=1', requires: 'course.create', label: 'Create a course' },
  },
  {
    label: 'Assessments',
    hint: 'Questions, marking and limits',
    icon: ClipboardList,
    view: { to: '/admin/assessments', requires: 'assessment.view', label: 'View all assessments' },
    create: {
      to: '/admin/assessments?new=1',
      requires: 'assessment.create',
      label: 'Create an assessment',
    },
  },
  {
    label: 'Certificates',
    /**
     * "Award one by hand" was wrong, and the plus went to a screen that cannot
     * do it.
     *
     * Nothing in this product issues a SINGLE certificate from a button:
     * `/admin/certificates` lists and revokes, and issuing happens either
     * automatically on course completion, from a course's own learners tab
     * when somebody signs a completion off, or in bulk from a sheet. The plus
     * now goes to the one of those that is a deliberate act you can start from
     * here — and is gated on the bulk permission it actually needs.
     */
    hint: 'Issued on completion, or in bulk',
    icon: Award,
    view: { to: '/admin/certificates', requires: 'certificate.view', label: 'View all certificates' },
    create: {
      to: '/admin/bulk/CERTIFICATE_ISSUANCE',
      requires: 'certificate.bulk_issue',
      label: 'Issue certificates in bulk',
    },
  },
  {
    label: 'Calendar',
    hint: 'Sessions and learning topics',
    icon: CalendarPlus,
    view: { to: '/calendar', requires: 'calendar.view', label: 'Open the calendar' },
    create: { to: '/calendar?new=1', requires: 'calendar.create', label: 'Schedule a topic' },
  },
];

/**
 * Question Reports, for a Trainer only.
 *
 * Deliberately NOT in the shared list. An Admin reaches this from the
 * attention band, where the row appears only while a report is open — which
 * was a decision taken earlier, and putting a permanent tile here would undo
 * it. A Trainer has no such row and no Content section either, so without this
 * the screen is unreachable for them.
 */
const TRAINER_QUESTION_REPORTS: QuickAction = {
  label: 'Question Reports',
  hint: 'Objections raised on your questions',
  icon: Flag,
  view: { to: '/admin/question-reports', requires: 'rebuttal.view', label: 'View question reports' },
};

/**
 * What a Trainer is offered, which is not what an Admin is offered.
 *
 * ── Certificates comes off ────────────────────────────────────────────────
 * The tile's whole purpose is the bulk-issue "+" beside it, and a Trainer
 * cannot issue certificates at all — theirs are awarded by a course's own
 * completion rule. The tile would be a view button onto a list they can do
 * nothing with, on a panel headed "start something new".
 *
 * ── Question Reports goes on ──────────────────────────────────────────────
 * Because nothing else reaches it for them: no Content section, and no
 * attention row.
 */
export function actionsFor(roleSlugs: string[]): QuickAction[] {
  if (!roleSlugs.includes(SYSTEM_ROLES.TRAINER)) return QUICK_ACTIONS;

  return [
    ...QUICK_ACTIONS.filter((action) => action.label !== 'Certificates'),
    TRAINER_QUESTION_REPORTS,
  ];
}

export function QuickActions({
  permissions,
  roleSlugs = [],
}: {
  permissions: string[];
  /** Empty for an Admin, so the shared list is used unchanged. */
  roleSlugs?: string[];
}) {
  const actions = gateActions(actionsFor(roleSlugs), permissions);

  if (actions.length === 0) return null;

  return (
    /*
      `h-full` and a column, so this card matches the taller one beside it.
      The rows then share whatever height is left rather than stacking at the
      top and leaving a block of empty card underneath — which is what made
      the pair look mismatched even when their outlines were the same size.
    */
    <Card className="flex h-full flex-col overflow-hidden">
      <div className="shrink-0 border-b px-4 py-3">
        <h2 className="text-[15px] font-semibold">Quick actions</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">View or start something new</p>
      </div>

      <ul className="flex flex-1 flex-col divide-y">
        {actions.map((action) => {
          const Icon = action.icon;

          /**
           * ── THE WHOLE ROW OPENS THE LIST ──────────────────────────────────
           *
           * The icon and the label used to be inert: only a 32px eye at the
           * far right actually navigated, so the obvious target — the big
           * green tile with the word "Courses" beside it — did nothing.
           *
           * They are a link now, and the eye stays beside them: the tile is
           * the big target somebody reaches for, the eye is the explicit one
           * that says the row goes somewhere. Same destination, deliberately.
           *
           * SIBLINGS, not nested: the plus stays a separate link outside this
           * one, because an anchor inside an anchor is invalid markup that
           * browsers silently unnest. That constraint is what kept the row
           * inert before — it is solved by putting the link around the icon
           * and text rather than around the row.
           *
           * `py-2.5` and an 8-unit tile: the text is two short lines, so the
           * ICON was setting this row's height — taller than the words beside
           * it needed, four times over.
           */
          const body = (
            <>
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-800 text-white">
                <Icon className="h-4 w-4" aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold">{action.label}</span>
                <span className="block truncate text-xs text-muted-foreground">{action.hint}</span>
              </span>
            </>
          );

          return (
            <li key={action.label} className="flex flex-1 items-stretch">
              {action.view ? (
                <Link
                  to={action.view.to}
                  title={action.view.label}
                  aria-label={action.view.label}
                  className="group flex min-w-0 flex-1 items-center gap-3 px-4 py-2.5 transition-colors hover:bg-muted/50"
                >
                  {body}
                </Link>
              ) : (
                /* Somebody who may create but not list. Rare, and the row has
                   nowhere to go — so it stays inert rather than pretending. */
                <span className="flex min-w-0 flex-1 items-center gap-3 px-4 py-2.5">{body}</span>
              )}

              <span className="flex shrink-0 items-center gap-1 pr-3">
                {/*
                  Kept beside the row it duplicates, by request.

                  It is the same destination as the row now — which is the
                  point of a pair: the tile is the big target, and the eye is
                  the explicit one, sitting where the plus is so the two
                  actions on a row read as a set rather than one control and
                  one mystery.
                */}
                {action.view && (
                  <Link
                    to={action.view.to}
                    title={action.view.label}
                    aria-label={action.view.label}
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <Eye className="h-4 w-4" aria-hidden />
                  </Link>
                )}

                {action.create && (
                  <Link
                    to={action.create.to}
                    title={action.create.label}
                    aria-label={action.create.label}
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <Plus className="h-4 w-4" aria-hidden />
                  </Link>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
/** The open-in-place arrow. Inline rather than an icon import for one glyph. */
const ArrowOut = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 14 14"
    className={className ?? 'h-3 w-3'}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    aria-hidden
  >
    <path d="M4 10 10 4M5.5 4H10v4.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
// ═══════════════════════════════════════════════════════════════════════════
// MANDATORY COURSE STATUS
// ═══════════════════════════════════════════════════════════════════════════

export function MandatoryCourseStatus({ courses }: { courses: AdminData['mandatoryCourses'] }) {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 border-b px-4 py-3">
        <ShieldCheck className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <h2 className="text-sm font-semibold">Mandatory course status</h2>
        <span className="ml-auto text-xs text-muted-foreground">Org-wide</span>
      </div>

      {courses.length === 0 ? (
        <div className="px-6 py-8 text-center">
          <p className="text-sm font-medium">No mandatory training assigned</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Mark a course mandatory, or assign one as required, and it appears here.
          </p>
        </div>
      ) : (
        <ul className="divide-y">
          {courses.map((course) => (
            <li key={course.id} className="px-4 py-3">
              <div className="flex items-center gap-3">
                <Link
                  to={`/admin/courses/${course.id}`}
                  className="min-w-0 flex-1 truncate text-[13px] font-medium hover:underline"
                >
                  {course.title}
                </Link>
                <span
                  className={cn(
                    'shrink-0 font-mono text-xs font-semibold tabular-nums',
                    course.percent >= 90
                      ? 'text-success'
                      : course.percent < 50
                        ? 'text-destructive'
                        : 'text-warning',
                  )}
                >
                  {Math.round(course.percent)}%
                </span>
              </div>

              <div className="mt-2 flex items-center gap-2.5">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                  <div
                    className={cn(
                      'h-full rounded-full transition-[width] duration-500',
                      course.percent >= 90
                        ? 'bg-success'
                        : course.percent < 50
                          ? 'bg-destructive'
                          : 'bg-warning',
                    )}
                    style={{ width: `${course.percent}%` }}
                  />
                </div>
                <span className="shrink-0 font-mono text-[11px] text-muted-foreground tabular-nums">
                  {course.completed} of {course.total}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// THE ADMIN'S OWN LEARNING, kept but demoted
// ═══════════════════════════════════════════════════════════════════════════

/**
 * An administrator still has training of their own.
 *
 * It was the whole top of their dashboard; now it is one line with a way
 * through to it. Removing it entirely would be the other mistake — mandatory
 * training is mandatory for the person who administers it too, and a
 * compliance figure they are personally missing from is a bad look.
 */
export function YourOwnLearning({
  outstanding,
  total,
  hoursThisMonth,
}: {
  outstanding: number;
  total: number;
  hoursThisMonth: string;
}) {
  return (
    <Card className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
      <Users className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <p className="text-[13px]">
        <span className="font-medium">Your own learning</span>
        <span className="text-muted-foreground">
          {' — '}
          {total === 0
            ? 'nothing mandatory assigned to you'
            : outstanding === 0
              ? 'all mandatory training complete'
              : `${outstanding} mandatory course${outstanding === 1 ? '' : 's'} outstanding`}
        </span>
      </p>

      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Clock className="h-3.5 w-3.5" aria-hidden />
        {hoursThisMonth} this month
      </span>

      <Link
        to="/my-courses"
        className="ml-auto flex shrink-0 items-center gap-1 text-[13px] font-medium text-brand-700 hover:underline"
      >
        <TrendingUp className="h-3.5 w-3.5" aria-hidden />
        Open My Courses
      </Link>
    </Card>
  );
}
